import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    IconButton,
    Box,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { ClientInfo } from '../../models/ClientInfo';
import { getAllClients, getClientsByCompanyName } from '../../services/clientService';

const ClientList: React.FC = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState<ClientInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchClients = async (search?: string) => {
        try {
            setLoading(true);
            const fetchedClients = search
                ? await getClientsByCompanyName(search)
                : await getAllClients();
            setClients(fetchedClients);
            setError(null);
        } catch (err) {
            setError('Failed to fetch clients');
            console.error('Error fetching clients:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                fetchClients(searchTerm);
            } else {
                fetchClients();
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleRowClick = (clientId: string) => {
        navigate(`/clients/${clientId}`);
    };

    if (loading && clients.length === 0) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            {error && (
                <Alert severity="error" action={
                    <IconButton
                        aria-label="refresh"
                        color="inherit"
                        size="small"
                        onClick={() => fetchClients()}
                    >
                        <RefreshIcon />
                    </IconButton>
                }>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Company Name</TableCell>
                            <TableCell>Primary Contact</TableCell>
                            <TableCell>CRM Entry ID</TableCell>
                            <TableCell>Created At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clients.map((client) => (
                            <TableRow
                                key={client.id}
                                onClick={() => handleRowClick(client.id)}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            >
                                <TableCell>{client.companyName}</TableCell>
                                <TableCell>{client.primaryContact}</TableCell>
                                <TableCell>{client.crmEntryId}</TableCell>
                                <TableCell>{client.createdAt.toLocaleDateString()}</TableCell>
                            </TableRow>
                        ))}
                        {clients.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No clients found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ClientList; 